import { Component, Inject, OnInit } from '@angular/core';
import { DataService } from '../data/data.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NumpadValue } from '../core/components/numpad.component';
import { updateCurrencyAmount } from '../core/components/currency-input-calculations';
import { PrinterService } from '../data/printer.service';
import { AuthUserService } from '../auth/auth-user.service';
import {
  isSPaymentPOSSwitchModeNotConnectedError,
  isSPaymentPOSTechnicalError,
  PaymentService as SPaymentService
} from '../data/s-payment.service';
import { ErrorNotificationsService } from '../core/error-handling/error-notifications.service';
import {APP_SETTINGS} from '../data/settings-data.provider';
import {Settings} from '../data/models';
import {PaymentService} from '../data/payment.service';
import {environment} from '../../environments/environment';
import {PosApiPaymentService} from '../data/pos-api-payment.service';

@Component({
    selector: 'app-cashbox-close-modal',
    template: `
    <div class="modal-header">
      <h4 class="modal-title" i18n>Kassenabschluss</h4>
      <button type="button" class="btn-close" aria-label="Close" i18n-aria-label
              (click)="closeModal()"></button>
    </div>
    <div class="modal-body">
      <div class="d-flex flex-column flex-lg-row">
        <div class="d-flex flex-column">
          <div class="d-flex flex-column align-items-center p-3 mb-2 mb-md-3">
            <div class="d-block w-100 d-md-none pb-3" i18n>Ihre Eingabe</div>
            <div class="input-group focus-input-group w-208px w-md-272px">
              <div class="input-group-text bg-white">
                <button type="button" class="btn-close" (click)="revertAmount()" [disabled]="dataService.cashBoxClosed"></button>
              </div>
              <app-currency-input class="form-control fs-4 form-control-lg border-start-0" [value]="insertedAmount"
                                  ngbAutofocus
                                  [disabled]="dataService.cashBoxClosed"
                                  (valueChange)="updateAmount($event)"></app-currency-input>
            </div>
          </div>
          <app-numpad theme="dark" [showDecimalZeroes]="true" [disabled]="dataService.cashBoxClosed" (buttonPress)="handleNumpadEvent($event)"></app-numpad>
        </div>

        <div class="border-end border-bottom border-medium"></div>

        <div class="vstack p-3 gap-3">
          <div class="d-none d-md-flex justify-content-between align-items-center flex-nowrap">
            <div class="d-flex flex-column flex-nowrap fs-5">
              <span i18n>Ihre Eingabe</span>
              <span class="text-muted fs-6" i18n>(Ist-Bestand)</span>
            </div>
            <span class="fs-4">
              {{insertedAmount | currency}}
            </span>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <span class="fs-5" i18n>Soll-Bestand</span>
            <span class="fs-4">
              {{dataService.cashBox.cashBalance | currency}}
            </span>
          </div>

          <div class="border-bottom border-medium d-none d-lg-block"></div>

          <div class="d-flex justify-content-between align-items-center" *ngIf="differenceAmount > 0">
            <span class="fs-5" i18n>Überschuss</span>
            <span class="fs-4 text-success">
              {{differenceAmount | currency}}
            </span>
          </div>

          <div class="d-flex justify-content-between align-items-center" *ngIf="differenceAmount < 0">
            <span class="fs-5" i18n>Fehlbetrag</span>
            <span class="fs-4 text-danger">
              {{differenceAmount | currency}}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-footer justify-content-center align-items-center p-3 p-md-4">
      <div *ngIf="isOPIPaymentEnabled() && !remoteTerminalAvailable">
        <div class="text-muted text-center py-1 py-md-4 fs-5 px-2" i18n>
          Terminal ist nicht verfügbar. Bitte überprüfen Sie den Terminalstatus.
        </div>
        <button type="button" class="btn btn-lg btn-warning text-white w-100 py-3" i18n
                (click)="checkTerminalStatus()" [appLoadingOverlay]="terminalStatusCheckInProgress">
          {{ "Überprüfung der Terminalverbindung" }}
        </button>
      </div>
      <button type="button" class="btn btn-lg btn-success text-white w-100 py-3" i18n [disabled]="loading"
              [appLoadingOverlay]="loading"
              *ngIf="!dataService.cashBoxClosed && (!isOPIPaymentEnabled() || (isOPIPaymentEnabled() && remoteTerminalAvailable))"
              (click)="closeCashBox()">
        Abschluss durchführen
      </button>

      <button type="button" class="btn btn-lg btn-info text-white w-100 py-3" i18n
              [disabled]="loading || !printService.isGeneralReportPrintEnabled"
              [appLoadingOverlay]="printing"
              *ngIf="dataService.cashBoxClosed"
              (click)="printSummaryReport()">
        Z-Bericht drucken
      </button>
    </div>
  `,
    styles: [`
    :host {
      display: block;
    }
  `],
    standalone: false
})
export class CashBoxCloseModalComponent implements OnInit {
  loading = false;
  printing = false;
  insertedAmount = 0;
  remoteTerminalAvailable: boolean = false;
  terminalStatusCheckInProgress: boolean = false;

  get differenceAmount(): number {
    return this.insertedAmount - this.dataService.cashBox.cashBalance;
  }

  constructor(
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    public activeModal: NgbActiveModal,
    public dataService: DataService,
    public printService: PrinterService,
    private readonly userService: AuthUserService,
    private readonly sPaymentService: SPaymentService,
    private readonly paymentService: PaymentService,
    private readonly posApiPaymentService: PosApiPaymentService,
    private readonly errorNotifications: ErrorNotificationsService,
  ) {}

  async ngOnInit() {
    if (this.isOPIPaymentEnabled()) {
      await this.checkTerminalStatus();
    }
  }

  handleNumpadEvent(value: NumpadValue) {
    const updatedAmount = updateCurrencyAmount(this.insertedAmount, value);
    this.updateAmount(updatedAmount);
  }

  updateAmount(value: number) {
    this.insertedAmount = value;
  }

  revertAmount() {
    this.insertedAmount = 0;
  }

  async closeCashBox() {
    this.loading = true;
    try {
      if (environment.runOnAndroidPaymentTerminal) {
        await this.closeCashBoxLocalTerminal();
        return;
      } else if (this.isOPIPaymentEnabled()) {
        await this.closeCashBoxOPI();
        return;
      } else if (this.isPosApiPaymentEnabled()) {
        await this.closeCashBoxPosApi();
        return;
      }

      await this.closeCashBoxSPOS();

    } finally {
      this.loading = false;
    }
  }

  private async closeCashBoxSPOS() {
    this.loading = true;
    try {
      const sposZReportResult = await this.createSPaymentZReport();
      await this.dataService.closeCashBox(this.insertedAmount, sposZReportResult?.merchantReceipt);
    }
    finally {
      this.loading = false;
    }
  }

  private async closeCashBoxOPI() {
    this.loading = true;
    try {
      const periodCloseResult = await this.paymentService.callPeriodCloseRemoteTerminalPayment();
      await this.dataService.closeCashBox(this.insertedAmount, undefined, periodCloseResult?.merchantReceipt);
    }
    finally {
      this.loading = false;
    }
  }

  private async closeCashBoxLocalTerminal() {
    this.loading = true;
    try {
      const periodCloseResult = await this.paymentService.callPeriodCloseLocalTerminalPayment();
      await this.dataService.closeCashBox(this.insertedAmount, undefined, periodCloseResult?.merchantReceipt);
    }
    finally {
      this.loading = false;
    }
  }

  private async closeCashBoxPosApi() {
    this.loading = true;
    try {
      await this.posApiPaymentService.callClosePeriod();
      await this.dataService.closeCashBox(this.insertedAmount, undefined, undefined);
    }
    finally {
      this.loading = false;
    }
  }

  /**
   * Handle S-Payment POS reconciliation attempt (Z-report) in an isolated way. In case of any
   * technical or non-technical failure, simply log the event and show the corresponding error message,
   * but the result should not have an effect on the cash box closing on Versacommerce.
   *    https://3.basecamp.com/3682674/buckets/33839768/card_tables/cards/6520943664#__recording_6523622675
   *    https://3.basecamp.com/3682674/buckets/33839768/card_tables/cards/6520943664#__recording_6524340582
   */
  private async createSPaymentZReport() {
    const hasSPOSConfigured= await this.sPaymentService.isSPaymentPOSAvailable();
    if (!hasSPOSConfigured) {
      return;
    }
    const errorMessage = $localize`Der Z-Report des S-Payment POS konnte nicht hergestellt werden. Bitte wenden Sie sich an den Support.`;
    try {
      const reconciliationResult = await this.sPaymentService.createZReport();
      if (reconciliationResult.isAccepted) {
        return reconciliationResult;
      }
      console.warn(errorMessage, reconciliationResult);
    } catch (error: any) {
      if (!isSPaymentPOSTechnicalError(error)) {
        throw error;
      }
      if (isSPaymentPOSSwitchModeNotConnectedError(error)) {
        const switchModeNotConnectedMessage = $localize`Die Verbindung zur S-Payment POS-App ist unterbrochen. Bitte melden Sie sich erneut an, um die Verbindung wiederherzustellen, und versuchen Sie es erneut.`;
        this.errorNotifications.showError(switchModeNotConnectedMessage);
        return;
      }
      console.error(errorMessage, error.data);
    }
    this.errorNotifications.showError(errorMessage);
    return;
  }

  async printSummaryReport() {
    this.printing = true;
    try {
      await this.printService.printCashboxSummaryReport();
    } finally {
      this.printing = false;
    }
  }

  closeModal() {
    this.activeModal.close();

    if (this.dataService.cashBoxClosed) {
      this.userService.logout();
    }
  }

  async checkTerminalStatus() {
    try {
      const paymentMethod = this.settings.paymentMethods.find(value => value.gateway === 'versa_commerce_pos_opi');
      this.terminalStatusCheckInProgress = true;
      this.remoteTerminalAvailable = await this.paymentService.checkTerminalAvailable(paymentMethod?.settings?.terminalIp!);
    } finally {
      this.terminalStatusCheckInProgress = false;
    }
  }

  isOPIPaymentEnabled() {
    return this.settings.paymentMethods.find(value => value.gateway === 'versa_commerce_pos_opi') !== undefined;
  }

  isPosApiPaymentEnabled() {
    return this.settings.paymentMethods.find(value => value.gateway === 'versa_commerce_pos_zvt') !== undefined;
  }

  isSPOSPaymentEnabled() {
    // currently only one terminal supported for cashbox closed, if opi payment is enabled spos close cashbox should not be available
    return !this.isOPIPaymentEnabled() && this.settings.paymentMethods.find(value => value.gateway === 'versa_commerce_pos_spos') !== undefined;
  }
}
